import React, { useState, useContext, useEffect } from "react";
import { Link as RouterLink, useLocation } from "react-router-dom";
import {
  Button,
  CssBaseline,
  TextField,
  Link,
  Box,
  Backdrop,
  CircularProgress,
  Grid,
  Typography,
  InputAdornment,
  IconButton,
  Container,
  Paper,
  useTheme,
  alpha,
  Divider
} from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import { styled } from '@mui/material/styles';
import useResponsive from '../../hooks/useResponsive';
import { i18n } from "../../translate/i18n";
import Page from '../../components/Page';
import { AuthContext } from "../../context/Auth/AuthContext";
import logo from '../../assets/icons/UniChatDark.svg'; 

// Styled components com adaptação para tema escuro
const LoginContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  minHeight: '100vh',
  background: theme.palette.mode === 'dark' 
    ? `linear-gradient(135deg, ${alpha(theme.palette.background.default, 0.9)} 0%, ${alpha(theme.palette.background.paper, 0.7)} 100%)`
    : `linear-gradient(135deg, #f6f9fc 0%, #edf2f7 100%)`,
  position: 'relative',
  overflow: 'hidden',
  [theme.breakpoints.down('md')]: {
    alignItems: 'center',
    justifyContent: 'center',
  },
}));

// Elementos decorativos abstratos para o fundo
const AbstractElement = styled(Box)(({ theme, size, top, left, color }) => ({
  position: 'absolute',
  width: size,
  height: size,
  borderRadius: '38% 62% 63% 37% / 41% 44% 56% 59%', // Forma orgânica
  background: color || theme.palette.primary.main,
  opacity: theme.palette.mode === 'dark' ? 0.07 : 0.1,
  top,
  left,
  animation: 'float 15s ease-in-out infinite',
  '@keyframes float': {
    '0%': { transform: 'translate(0px, 0px) rotate(0deg)' },
    '50%': { transform: 'translate(20px, 20px) rotate(5deg)' },
    '100%': { transform: 'translate(0px, 0px) rotate(0deg)' },
  },
}));

const GridPattern = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  right: 0,
  bottom: 0,
  width: '60%',
  opacity: theme.palette.mode === 'dark' ? 0.05 : 0.03,
  backgroundImage: `linear-gradient(${theme.palette.primary.main} 1px, transparent 1px), linear-gradient(90deg, ${theme.palette.primary.main} 1px, transparent 1px)`,
  backgroundSize: '20px 20px',
  zIndex: 0,
}));

// Container principal com efeito de vidro ajustado para dark mode
const GlassLoginCard = styled(Paper)(({ theme }) => ({
  width: '90%',
  maxWidth: 1100,
  minHeight: 600,
  margin: 'auto',
  display: 'flex',
  borderRadius: 16,
  overflow: 'hidden',
  boxShadow: theme.palette.mode === 'dark' 
    ? '0 25px 50px -12px rgba(0, 0, 0, 0.5)'
    : '0 25px 50px -12px rgba(0, 0, 0, 0.15)',
  background: theme.palette.mode === 'dark'
    ? alpha(theme.palette.background.paper, 0.8)
    : 'rgba(255, 255, 255, 0.95)',
  backdropFilter: 'blur(20px)',
  border: theme.palette.mode === 'dark'
    ? `1px solid ${alpha(theme.palette.common.white, 0.1)}`
    : `1px solid rgba(255, 255, 255, 0.2)`,
  position: 'relative',
  zIndex: 5,
  [theme.breakpoints.down('md')]: {
    maxWidth: 500,
    flexDirection: 'column',
    minHeight: 'unset',
  },
}));

// Seção da marca/CRM - Parte impactante da tela
const BrandSection = styled(Box)(({ theme }) => ({
  flex: '1 1 60%',
  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
  display: 'flex',
  flexDirection: 'column',
  color: '#fff',
  position: 'relative',
  padding: theme.spacing(6),
  overflow: 'hidden',
  [theme.breakpoints.down('md')]: {
    padding: theme.spacing(4, 4, 5),
  },
}));

// Efeito de pontilhado para a seção da marca
const DotPattern = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  backgroundImage: `radial-gradient(rgba(255, 255, 255, 0.15) 2px, transparent 2px)`,
  backgroundSize: '30px 30px',
  zIndex: 0,
}));

// Seção de formulário
const LoginSection = styled(Box)(({ theme }) => ({
  flex: '1 1 40%',
  padding: theme.spacing(6),
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  position: 'relative',
  zIndex: 2,
  [theme.breakpoints.down('md')]: {
    padding: theme.spacing(4),
  },
}));

// Visual de CRM - Componentes
const MetricCard = styled(Box)(({ theme, bgColor }) => ({
  background: bgColor || alpha('#fff', 0.1),
  padding: theme.spacing(2.5),
  borderRadius: 12,
  marginBottom: theme.spacing(2),
  backdropFilter: 'blur(10px)',
  border: '1px solid rgba(255, 255, 255, 0.1)',
  transition: 'all 0.3s ease',
  transform: 'translateY(0)',
  cursor: 'default',
  '&:hover': {
    transform: 'translateY(-5px)',
    boxShadow: '0 10px 25px -10px rgba(0, 0, 0, 0.3)',
  },
}));

const DataBar = styled(Box)(({ theme, value, color }) => ({
  height: 8,
  width: '100%',
  background: alpha('#fff', 0.2),
  borderRadius: 4,
  position: 'relative',
  overflow: 'hidden',
  marginTop: theme.spacing(1),
  '&:after': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    height: '100%',
    width: value,
    background: color || '#fff',
    borderRadius: 4,
  },
}));

// Campos de formulário estilizados com adaptação para dark mode
const StyledTextField = styled(TextField)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  '& .MuiOutlinedInput-root': {
    borderRadius: 12,
    backgroundColor: theme.palette.mode === 'dark' 
      ? alpha(theme.palette.common.white, 0.05)
      : alpha(theme.palette.common.black, 0.02),
    transition: 'all 0.2s ease-out',
    height: 56,
    '&:hover': {
      backgroundColor: theme.palette.mode === 'dark' 
        ? alpha(theme.palette.common.white, 0.08)
        : alpha(theme.palette.common.black, 0.04),
    },
    '&.Mui-focused': {
      backgroundColor: theme.palette.mode === 'dark'
        ? alpha(theme.palette.common.white, 0.07)
        : theme.palette.common.white,
      boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.4 : 0.25)}`,
    }
  },
  '& .MuiInputBase-input': {
    padding: '16.5px 14px',
    color: theme.palette.text.primary,
  },
  '& .MuiInputLabel-root': {
    color: theme.palette.text.secondary,
  },
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: theme.palette.mode === 'dark'
      ? alpha(theme.palette.common.white, 0.15)
      : alpha(theme.palette.common.black, 0.1),
  },
  [theme.breakpoints.down('sm')]: {
    marginBottom: theme.spacing(2.5),
  },
}));

// Botão principal com efeito de gradiente
const GradientButton = styled(Button)(({ theme }) => ({
  padding: theme.spacing(1.5, 0),
  marginTop: theme.spacing(2),
  borderRadius: 12,
  height: 56,
  fontSize: 16,
  fontWeight: 600,
  backgroundImage: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
  backgroundSize: '200% 200%',
  transition: 'all 0.3s ease',
  boxShadow: theme.palette.mode === 'dark'
    ? '0 10px 25px -5px rgba(0, 0, 0, 0.3)'
    : '0 10px 25px -5px rgba(0, 0, 0, 0.15)',
  '&:hover': {
    backgroundPosition: 'right center',
    boxShadow: theme.palette.mode === 'dark'
      ? '0 10px 25px -3px rgba(0, 0, 0, 0.4)'
      : '0 10px 25px -3px rgba(0, 0, 0, 0.2)',
    transform: 'translateY(-3px)',
  },
  '&:active': {
    transform: 'translateY(0)',
  },
}));

// Componente principal
const Login = () => {
  const theme = useTheme();
  const mdUp = useResponsive('up', 'md');
  const lgUp = useResponsive('up', 'lg');
  const isDarkMode = theme.palette.mode === 'dark';

  const [user, setUser] = useState({ 
    email: "", 
    password: "", 
    showPassword: false 
  });
  
  const [loading, setLoading] = useState(false);

  const { handleLogin } = useContext(AuthContext);

  const handleChangeInput = (e) => {
    setUser({ ...user, [e.target.name]: e.target.value });
  };

  const handlSubmit = (e) => {
    e.preventDefault();
    setLoading(true);
    
    // Simulando tempo de resposta para demonstrar o loading
    setTimeout(() => {
      handleLogin(user);
      setLoading(false);
    }, 800);
  };

  const handleClickShowPassword = () => {
    setUser({ ...user, showPassword: !user.showPassword });
  };

  const location = useLocation();
  const { email: urlEmail, token: urlToken } = Object.fromEntries(
    new URLSearchParams(location.search)
  );

  useEffect(() => {
    if (urlEmail && urlToken) {
      try {
        handleLogin({ email: urlEmail, token: urlToken });
      } catch (err) {
        console.error({ err });
      }
    }
  }, []);

  return (
    <Page title="Login">
      <CssBaseline />
      
      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      
      <LoginContainer>
        {/* Elementos decorativos - visíveis apenas em desktop */}
        {mdUp && (
          <>
            <AbstractElement 
              size="600px" 
              top="-300px" 
              left="-200px" 
              color={theme.palette.primary.main} 
            />
            <AbstractElement 
              size="500px" 
              top="70%" 
              left="60%" 
              color={theme.palette.secondary.main} 
            />
            <AbstractElement 
              size="300px" 
              top="10%" 
              left="75%" 
              color={theme.palette.primary.dark} 
            />
            <GridPattern />
          </>
        )}
        
        <Container maxWidth="lg" sx={{ 
          display: 'flex', 
          alignItems: 'center',
          height: '100vh',
          position: 'relative',
          zIndex: 2
        }}>
          {/* Card de login com layout responsivo */}
          <GlassLoginCard elevation={isDarkMode ? 4 : 0}>
            {/* Seção da marca/mensagens de marketing - visível apenas em desktop */}
            {mdUp && (
              <BrandSection>
                <DotPattern />
                <Box sx={{ position: 'relative', zIndex: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <Box 
                      component="img" 
                      src={logo} 
                      alt="Logo" 
                      sx={{ height: 60 }} 
                    />
                    <Typography variant="h5" fontWeight="bold" sx={{ color: 'white' }}>
                      UniChat
                    </Typography>
                  </Box>
                  
                  <Typography variant="h3" fontWeight="800" sx={{ 
                    color: 'white', 
                    mb: 2,
                    textShadow: '0 2px 10px rgba(0,0,0,0.1)'
                  }}>
                    {i18n.t("login.welcome")}
                  </Typography>
                  
                  <Typography variant="h6" sx={{ 
                    color: 'white', 
                    opacity: 0.9,
                    fontWeight: 400,
                    maxWidth: 500,
                    mb: 8
                  }}>
                    Centralize todos os seus atendimentos WhatsApp em um único lugar. Gerencie múltiplos atendentes, automatize vendas e ofereça suporte de qualidade.
                  </Typography>
                  
                  <Box sx={{ display: 'grid', gridTemplateColumns: { md: '1fr 1fr', xs: '1fr' }, gap: 3 }}>
                    <MetricCard>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="subtitle1" fontWeight="600" sx={{ color: 'white' }}>
                          Tempo de Resposta
                        </Typography>
                        <Typography variant="subtitle1" fontWeight="bold" sx={{ color: 'white' }}>
                          -72%
                        </Typography>
                      </Box>
                      <DataBar value="72%" color="rgba(255,255,255,0.9)" />
                    </MetricCard>
                    
                    <MetricCard>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="subtitle1" fontWeight="600" sx={{ color: 'white' }}>
                          Satisfação Cliente
                        </Typography>
                        <Typography variant="subtitle1" fontWeight="bold" sx={{ color: 'white' }}>
                          97%
                        </Typography>
                      </Box>
                      <DataBar value="97%" color="rgba(255,255,255,0.9)" />
                    </MetricCard>
                  </Box>
                  
                  <Box sx={{ mt: 3, display: { xs: 'none', md: 'block' } }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 4, mt: 6 }}>
                      <Box>
                        <Typography 
                          variant="h3" 
                          fontWeight="800" 
                          sx={{ color: 'white', textShadow: '0 2px 10px rgba(0,0,0,0.2)' }}
                        >
                          5x
                        </Typography>
                        <Typography variant="subtitle1" sx={{ color: 'white', opacity: 0.8 }}>
                          Mais atendimentos por agente
                        </Typography>
                      </Box>
                      
                      <Divider orientation="vertical" flexItem sx={{ 
                        borderColor: 'rgba(255,255,255,0.2)', 
                        height: 70 
                      }} />
                      
                      <Box>
                        <Typography 
                          variant="h3" 
                          fontWeight="800" 
                          sx={{ color: 'white', textShadow: '0 2px 10px rgba(0,0,0,0.2)' }}
                        >
                          +72%
                        </Typography>
                        <Typography variant="subtitle1" sx={{ color: 'white', opacity: 0.8 }}>
                          Na conversão de vendas
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </Box>
              </BrandSection>
            )}
            
            {/* Seção de login - versão mobile otimizada quando em telas pequenas */}
            <LoginSection sx={{
              padding: mdUp ? theme.spacing(6) : theme.spacing(4, 3),
              width: '100%'
            }}>
              {/* Logo e título apenas para mobile */}
              {!mdUp && (
                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  alignItems: 'center',
                  mb: 4
                }}>
                  <Box 
                    component="img" 
                    src={logo} 
                    alt="Logo" 
                    sx={{ height: 60, mb: 2 }}
                  />
                  <Typography 
                    variant="h5" 
                    fontWeight="700" 
                    color="primary"
                    textAlign="center"
                  >
                    UniChat
                  </Typography>
                  <Typography 
                    variant="body2" 
                    color="text.secondary" 
                    textAlign="center" 
                    sx={{ mt: 1 }}
                  >
                    Plataforma de atendimento via WhatsApp
                  </Typography>
                </Box>
              )}
              
              <Box>
                <Typography 
                  variant={mdUp ? "h4" : "h5"} 
                  fontWeight="700" 
                  color="text.primary" 
                  gutterBottom
                  textAlign={mdUp ? "left" : "center"}
                >
                  {i18n.t("login.formTitle")}
                </Typography>
                
                <Typography 
                  variant="body1" 
                  color="text.secondary" 
                  sx={{ mb: 5 }}
                  textAlign={mdUp ? "left" : "center"}
                >
                  {i18n.t("login.details")}
                </Typography>
                
                <form noValidate onSubmit={handlSubmit}>
                  <StyledTextField
                    variant="outlined"
                    required
                    fullWidth
                    id="email"
                    placeholder={i18n.t("login.form.email")}
                    name="email"
                    value={user.email}
                    onChange={handleChangeInput}
                    autoComplete="email"
                    autoFocus
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <EmailOutlinedIcon sx={{ color: theme.palette.text.secondary }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                  
                  <StyledTextField
                    variant="outlined"
                    required
                    fullWidth
                    name="password"
                    placeholder={i18n.t("login.form.password")}
                    id="password"
                    type={user.showPassword ? 'text' : 'password'}
                    value={user.password}
                    onChange={handleChangeInput}
                    autoComplete="current-password"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LockOutlinedIcon sx={{ color: theme.palette.text.secondary }} />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="toggle password visibility"
                            onClick={handleClickShowPassword}
                            edge="end"
                            sx={{ color: theme.palette.mode === 'dark' ? alpha(theme.palette.common.white, 0.7) : undefined }}
                          >
                            {user.showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                  
                  <GradientButton
                    type="submit"
                    fullWidth
                    variant="contained"
                    disableElevation
                  >
                    {i18n.t("login.buttons.submit")}
                  </GradientButton>
                  
                  <Box sx={{ mt: 4, textAlign: 'center' }}>
                    <Link
                      component={RouterLink}
                      to="/recovery"
                      variant="body2"
                      underline="hover"
                      sx={{ 
                        color: theme.palette.text.secondary,
                        fontWeight: 500,
                        transition: 'color 0.2s',
                        '&:hover': { color: theme.palette.primary.main }
                      }}
                    >
                      {i18n.t("login.buttons.recovery")}
                    </Link>
                  </Box>
                </form>
              </Box>
            </LoginSection>
          </GlassLoginCard>
        </Container>
      </LoginContainer>
    </Page>
  );
};

export default Login;